import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  CloseRounded,
  ErrorOutlineRounded,
  MailOutlineRounded,
  RefreshRounded,
  SearchRounded,
} from '@mui/icons-material';
import {
  getEmailLogCategories,
  getEmailLogs,
  getEmailLogStatuses,
  getFacilitiyV2,
} from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

const BRAND = '#0c5bab';

const titleCase = (value = '') =>
  String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '–';

const statusColor = (status = '') => {
  const s = String(status).toUpperCase();
  if (['SENT', 'SUCCESS', 'SUCCESSFUL', 'DELIVERED'].includes(s)) return '#2e7d32';
  if (['PENDING', 'QUEUED', 'RETRYING'].includes(s)) return '#ed6c02';
  if (['FAILED', 'ERROR', 'BOUNCED'].includes(s)) return '#d32f2f';
  return '#757575';
};

// Email bodies are HTML from the server; show them in a sandboxed frame (no scripts)
const emailDocument = (html) => `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: https:; style-src 'unsafe-inline';">
<style>body{font-family:Roboto,Helvetica,Arial,sans-serif;font-size:14px;color:#1f2d33;margin:16px;line-height:1.5}img{max-width:100%}</style>
</head><body>${html || '<p style="color:#888">This email has no body.</p>'}</body></html>`;

export default function EmailLogs() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [facility, setFacility] = useState(null);

  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    getEmailLogCategories().then(setCategories).catch(() => setCategories([]));
    getEmailLogStatuses().then(setStatuses).catch(() => setStatuses([]));
    getFacilitiyV2('', '', '', '', '', '', '', '', 1, 1000)
      .then((res) => setFacilities(res?.result?.data || []))
      .catch(() => setFacilities([]));
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [search, category, status, facility, rowsPerPage]);

  const fetchLogs = useCallback(async () => {
    // The email log needs a category; wait until one is chosen
    if (!category) {
      setRows([]);
      setTotal(null);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, total: count } = await getEmailLogs({
        emailCategory: category,
        search,
        facilityId: facility?.facilityId || '',
        emailStatus: status,
        start: page + 1,
        stop: rowsPerPage,
      });
      setRows(data);
      setTotal(count ?? null);
    } catch (e) {
      setRows([]);
      setTotal(null);
      setError(e?.message || "Couldn't load the email log. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [category, search, facility, status, page, rowsPerPage]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // The list endpoint may not return a total; allow "next page" while the page is full
  const count =
    total != null && total > rows.length ? total : rows.length === rowsPerPage ? -1 : page * rowsPerPage + rows.length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(BRAND, 0.08),
            color: BRAND,
          }}
        >
          <MailOutlineRounded />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            Email log
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Emails the system has sent, who received them, and whether they were delivered.
          </Typography>
        </Box>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search recipient or subject"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <MenuItem value="" disabled>
              Choose a category
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {titleCase(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {statuses.map((s) => (
              <MenuItem key={s} value={s}>
                {titleCase(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          options={facilities}
          value={facility}
          onChange={(_, v) => setFacility(v)}
          getOptionLabel={(o) => o?.facilityName || ''}
          isOptionEqualToValue={(o, v) => o.facilityId === v.facilityId}
          renderInput={(params) => <TextField {...params} label="Facility" placeholder="All facilities" />}
          sx={{ width: 240 }}
        />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchLogs} disabled={loading}>
              <RefreshRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Nothing chosen yet: prompt instead of an empty request */}
      {!category ? (
        <Paper
          elevation={0}
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(BRAND, 0.08),
              color: BRAND,
            }}
          >
            <MailOutlineRounded sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Choose an email category
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: categories.length ? 2.5 : 0 }}>
            Pick the kind of email you want to look at, then narrow it down by status or facility.
          </Typography>
          {categories.length > 0 && (
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ maxWidth: 640, mx: 'auto' }}>
              {categories.map((c) => (
                <Chip
                  key={c}
                  label={titleCase(c)}
                  onClick={() => setCategory(c)}
                  variant="outlined"
                  sx={{ fontWeight: 600, '&:hover': { borderColor: BRAND, color: BRAND } }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      ) : (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px' }}>
        {loading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
              {error}
            </Alert>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', bgcolor: '#fafbfc', whiteSpace: 'nowrap' } }}>
                  <TableCell>Sent</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No emails found for these filters
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const color = statusColor(r.status);
                    return (
                      <TableRow
                        key={r.emailLogId}
                        hover
                        onClick={() => setViewing(r)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.entryDate)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.receiverName || r.receiverEmail || '–'}
                          </Typography>
                          {r.receiverName && (
                            <Typography variant="caption" color="text.secondary">
                              {r.receiverEmail}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap title={r.emailSubject}>
                            {r.emailSubject || '–'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.emailCategory ? titleCase(r.emailCategory) : '–'}</TableCell>
                        <TableCell>{r.facilityName || '–'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              size="small"
                              label={r.status ? titleCase(r.status) : 'Unknown'}
                              sx={{ fontWeight: 600, height: 22, bgcolor: alpha(color, 0.1), color }}
                            />
                            {r.failureDescription && (
                              <Tooltip title={r.failureDescription}>
                                <ErrorOutlineRounded sx={{ fontSize: 18, color: '#d32f2f' }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
      )}

      {/* Email preview */}
      <Dialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', height: '85vh' } }}
      >
        {viewing && (
          <>
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {viewing.emailSubject || '(No subject)'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  To {viewing.receiverName ? `${viewing.receiverName} <${viewing.receiverEmail}>` : viewing.receiverEmail}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(viewing.entryDate)}
                  {viewing.emailCategory ? ` · ${titleCase(viewing.emailCategory)}` : ''}
                  {viewing.facilityName ? ` · ${viewing.facilityName}` : ''}
                </Typography>
              </Box>
              <IconButton onClick={() => setViewing(null)} aria-label="Close">
                <CloseRounded />
              </IconButton>
            </Stack>
            {viewing.failureDescription && (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                Not delivered: {viewing.failureDescription}
              </Alert>
            )}
            <DialogContent sx={{ p: 0, display: 'flex' }}>
              <Box
                component="iframe"
                title="Email content"
                sandbox=""
                srcDoc={emailDocument(viewing.emailBody)}
                sx={{ flex: 1, width: '100%', border: 0 }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
